<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SelectButton from 'primevue/selectbutton'
import BrandMark from '@/components/BrandMark.vue'
import ThemeToggle from '@/components/layout/ThemeToggle.vue'
import { setLocale, SUPPORTED_LOCALES, type Locale } from '@/i18n'
import { SITE, telHref } from '@/publicsite'

/**
 * The frame of the public site (web-app PublicNav + PublicFooter): landing, legal
 * pages, support and enrollment. Everything it shows comes from this origin — the
 * lockout profile's web filter on a locked iPhone allows nothing else.
 */

const { t, locale } = useI18n()
const menuOpen = ref(false)

const localeOptions = SUPPORTED_LOCALES.map((code) => ({ label: code.toUpperCase(), value: code }))

const links = [
  { to: '/#employee-intake', key: 'public.nav.employeeIntake' },
  { to: '/#how-to-register', key: 'public.nav.howToRegister' },
  { to: '/support', key: 'public.nav.support' },
  { to: '/privacy', key: 'public.nav.privacy' },
]

function changeLocale(value: Locale | null) {
  if (value) {
    setLocale(value)
  }
}
</script>

<template>
  <div class="site">
    <header class="site__header">
      <RouterLink to="/" class="site__brand">
        <BrandMark />
        <span class="site__wordmark">LiverPro</span>
      </RouterLink>
      <nav class="site__nav" :class="{ 'site__nav--open': menuOpen }" @click="menuOpen = false">
        <RouterLink v-for="link in links" :key="link.to" :to="link.to" class="site__link">{{ t(link.key) }}</RouterLink>
        <RouterLink to="/terms" class="site__link site__link--mobile">{{ t('public.nav.terms') }}</RouterLink>
      </nav>
      <div class="site__actions">
        <ThemeToggle />
        <SelectButton
          :model-value="locale"
          :options="localeOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          size="small"
          @update:model-value="changeLocale"
        />
        <RouterLink to="/login" class="site__console">{{ t('public.nav.adminPanel') }}</RouterLink>
        <button type="button" class="site__menu pi pi-bars" :aria-label="t('public.nav.menu')" @click="menuOpen = !menuOpen" />
      </div>
    </header>

    <main class="site__content">
      <slot />
    </main>

    <footer class="site__footer">
      <div class="site__footer-grid">
        <div>
          <div class="site__brand"><BrandMark /><span class="site__wordmark">LiverPro</span></div>
          <p class="site__muted">{{ t('public.footer.orgDescription') }}</p>
        </div>
        <div>
          <h3>{{ t('public.footer.platformTitle') }}</h3>
          <RouterLink to="/#employee-intake">{{ t('public.footer.employeeBlank') }}</RouterLink>
          <RouterLink to="/#how-to-register">{{ t('public.footer.howToRegister') }}</RouterLink>
          <RouterLink to="/login">{{ t('public.footer.adminLogin') }}</RouterLink>
        </div>
        <div>
          <h3>{{ t('public.footer.legalTitle') }}</h3>
          <RouterLink to="/privacy">{{ t('public.footer.privacyPolicy') }}</RouterLink>
          <RouterLink to="/terms">{{ t('public.footer.termsOfService') }}</RouterLink>
          <RouterLink to="/support">{{ t('public.footer.supportAndSla') }}</RouterLink>
        </div>
        <div>
          <h3>{{ t('public.footer.contactsTitle') }}</h3>
          <a v-for="phone in SITE.phones" :key="phone" :href="telHref(phone)">{{ phone }}</a>
          <a :href="`mailto:${SITE.email}`">{{ SITE.email }}</a>
        </div>
      </div>
      <div class="site__requisites">
        <strong>{{ t('public.footer.requisitesTitle') }}</strong>
        <span>{{ t('public.footer.companyNameLabel') }} {{ SITE.company }}</span>
        <span>{{ t('public.footer.innLabel') }} {{ SITE.inn }}</span>
        <span>{{ t('public.support.legalAddressDesc') }}</span>
      </div>
      <p class="site__notice">{{ t('public.footer.appleNotice') }} {{ t('public.footer.platformNotice') }}</p>
      <p class="site__notice">© {{ new Date().getFullYear() }} {{ SITE.company }}</p>
    </footer>
  </div>
</template>

<style scoped>
.site { display: flex; flex-direction: column; min-height: 100vh; background: var(--surface-page); }
.site__header {
  position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: var(--space-24);
  height: 64px; padding: 0 var(--page-gutter); background: var(--surface-page); border-bottom: 1px solid var(--border-hairline);
}
.site__brand { display: flex; align-items: center; gap: var(--space-8); color: var(--text-primary); text-decoration: none; }
.site__wordmark { font: var(--fw-semibold) 16px/20px var(--font-sans); letter-spacing: -0.03em; }
.site__nav { display: flex; gap: var(--space-20); flex: 1; }
.site__link { color: var(--text-secondary); text-decoration: none; white-space: nowrap; }
.site__link:hover, .site__link.router-link-exact-active { color: var(--text-primary); }
.site__link--mobile { display: none; }
.site__actions { display: flex; align-items: center; gap: var(--space-12); }
.site__console {
  padding: var(--space-6) var(--space-12); border-radius: var(--radius-8); background: var(--surface-inverse);
  color: var(--white); text-decoration: none; white-space: nowrap; font: var(--text-body-medium);
}
.site__menu { display: none; border: 0; background: none; font-size: 18px; cursor: pointer; color: var(--text-primary); }
.site__content { flex: 1; }
.site__footer { padding: var(--space-28) var(--page-gutter); border-top: 1px solid var(--border-hairline); background: var(--surface-subtle); }
.site__footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: var(--space-24); max-width: 1200px; margin: 0 auto; }
.site__footer-grid h3 { margin: 0 0 var(--space-8); font: var(--text-small-medium); color: var(--text-secondary); }
.site__footer-grid a { display: block; margin-bottom: var(--space-6); color: var(--text-primary); text-decoration: none; }
.site__footer-grid a:hover { text-decoration: underline; }
.site__muted { color: var(--text-secondary); max-width: 40ch; }
.site__requisites {
  display: flex; flex-wrap: wrap; gap: var(--space-8) var(--space-20); max-width: 1200px; margin: var(--space-24) auto 0;
  font: var(--text-small); color: var(--text-secondary);
}
.site__requisites strong { color: var(--text-primary); font-weight: 500; }
.site__notice { max-width: 1200px; margin: var(--space-12) auto 0; font: var(--text-small); color: var(--text-tertiary); }

@media (max-width: 900px) {
  .site__nav {
    display: none; position: absolute; top: 64px; left: 0; right: 0; flex-direction: column; gap: var(--space-12);
    padding: var(--space-16) var(--page-gutter); background: var(--surface-page); border-bottom: 1px solid var(--border-hairline);
  }
  .site__nav--open { display: flex; }
  .site__link--mobile { display: inline; }
  .site__console { display: none; }
  .site__menu { display: inline-block; }
  .site__footer-grid { grid-template-columns: 1fr 1fr; }
}
</style>
